import { useEffect, useRef, useState } from 'react';
import { navKey } from './session';

/**
 * P13: the unsaved values of an edit form wait for the way back ("Wstecz" from another screen, a reload). One draft
 * per form and record in the tab's session storage, with the record's version: on the same version the form simply
 * opens with them; when the record changed meanwhile, the form opens with the stored record and asks
 * "Masz niezapisane zmiany z wcześniej — Przywróć / Odrzuć". Saving or discarding the form forgets the draft.
 */

/** Under the signed-in account (`navKey`): another person at the same computer never sees it. */
const storageKey = (key: string) => navKey(`draft:${key}`);

/** `base`: the values the form opened with, so "Przywróć" takes over only what the person changed. */
type Stored<T> = { version: number; value: T; base: T };

const plain = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** Three-way: what the person changed (`mine` against `base`) on top of the record as it is now (`now`). */
export function mergeDraft<T>(base: T, mine: T, now: T): T {
  if (JSON.stringify(mine) === JSON.stringify(base)) return now;
  if (plain(base) && plain(mine) && plain(now)) {
    const out: Record<string, unknown> = { ...now };
    for (const k of Object.keys(mine)) out[k] = mergeDraft(base[k], mine[k], now[k]);
    return out as T;
  }
  return mine;
}

function read<T>(key: string): Stored<T> | null {
  try {
    const raw = sessionStorage.getItem(storageKey(key));
    const parsed = raw ? (JSON.parse(raw) as Stored<T>) : null;
    return parsed && typeof parsed.version === 'number' && 'value' in parsed && 'base' in parsed ? parsed : null;
  } catch {
    return null;
  }
}

function write<T>(key: string, stored: Stored<T>) {
  try {
    sessionStorage.setItem(storageKey(key), JSON.stringify(stored));
  } catch {
    // storage full or blocked: the draft lives only while the form is open
  }
}

function remove(key: string) {
  try {
    sessionStorage.removeItem(storageKey(key));
  } catch {
    // nothing stored then
  }
}

/** A form closed from outside (the panel's "Zamknij", Esc): what was typed in it is given up, as with "Anuluj". */
export function dropDraft(key: string) {
  remove(key);
}

export type DraftKeeper<T> = {
  /** The values to open the form with (a draft of this very version), or undefined. */
  initial: T | undefined;
  /** Values typed on an older version of the record, waiting for "Przywróć" / "Odrzuć". */
  older: T | null;
  /** Call on every render with the form's current values. */
  track: (value: T) => void;
  /** The form was saved or discarded: forget the draft. */
  clear: () => void;
  restore: () => T | null;
  discard: () => void;
};

/** `key`: the form and the record, e.g. `catalog.item:<id>` (`…:new` for adding); null keeps nothing. */
export function useFormDraft<T>(key: string | null, version: number): DraftKeeper<T> {
  const [start] = useState(() => {
    const saved = key ? read<T>(key) : null;
    if (!saved) return { initial: undefined, older: null, base: undefined };
    return saved.version === version ? { initial: saved.value, older: null, base: saved.base } : { initial: undefined, older: saved, base: undefined };
  });
  const [older, setOlder] = useState<Stored<T> | null>(start.older);
  const tracked = useRef<{ value: T } | null>(null);
  /** The values the form opened with (or had when last saved): no draft while they stay so. */
  const baseline = useRef<string | null>(null);
  /** What the draft was typed over (kept from a restored draft). */
  const base = useRef<{ value: T } | null>(start.base !== undefined ? { value: start.base } : null);
  const versionNow = useRef(version);
  versionNow.current = version;

  useEffect(() => {
    if (!key || !tracked.current) return;
    const json = JSON.stringify(tracked.current.value);
    if (baseline.current === null) {
      baseline.current = json;
      base.current ??= { value: tracked.current.value };
    } else if (json !== baseline.current) write(key, { version: versionNow.current, value: tracked.current.value, base: base.current!.value });
  });

  return {
    initial: start.initial,
    older: older?.value ?? null,
    track: (value) => {
      tracked.current = { value };
    },
    clear: () => {
      if (!key) return;
      remove(key);
      // a form that stays open (a person after "Zapisz") starts again from what it shows after this render
      baseline.current = null;
      base.current = null;
    },
    restore: () => {
      setOlder(null);
      if (!older || !tracked.current) return null;
      // the form sets it; the next render stores it as this version's draft (over this version's values)
      return mergeDraft(older.base, older.value, tracked.current.value);
    },
    discard: () => {
      setOlder(null);
      if (!key) return;
      // only the old draft: values typed since then (already stored on this version) stay
      const saved = read<T>(key);
      if (saved && saved.version !== versionNow.current) remove(key);
    },
  };
}

/** "Masz niezapisane zmiany z wcześniej — Przywróć / Odrzuć" above a form whose record changed meanwhile. */
export function DraftNotice<T>({ keeper, onRestore }: { keeper: DraftKeeper<T>; onRestore: (value: T) => void }) {
  if (keeper.older === null) return null;
  return (
    <div className="notice draft-notice" role="status">
      <span>Masz niezapisane zmiany z wcześniej, a w międzyczasie ten rekord się zmienił.</span>
      <span className="draft-notice-actions">
        <button
          className="btn small"
          type="button"
          onClick={() => {
            const value = keeper.restore();
            if (value !== null) onRestore(value);
          }}
        >
          Przywróć
        </button>
        <button className="btn small ghost" type="button" onClick={keeper.discard}>
          Odrzuć
        </button>
      </span>
    </div>
  );
}
